"use client";

import * as React from "react";
import ReactDOM from "react-dom";
import { cn } from "@/lib/utils";
import ButtonV2 from "./ButtonV2";

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  group?: string;
  icon?: React.ReactNode;
}

interface ComboboxV2Props {
  options: ComboboxOption[];
  value: string;
  onChange: (_value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  label?: string;
  ariaLabel?: string;
  helperText?: string;
  errorText?: string;
  successText?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  /** Called on every search-input keystroke — enables async/server-side filtering */
  onSearch?: (_query: string) => void;
  /** When true, skips internal client-side filtering (options already filtered externally) */
  isAsync?: boolean;
  /** When true, allows typing a custom value not present in the options list */
  allowCustomValue?: boolean;
}

interface ComboboxShellProps {
  ariaLabel?: string;
  className?: string;
  disabled: boolean;
  displayLabel: string | null;
  dropdown: React.ReactNode;
  errorText?: string;
  handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement>;
  helperText?: string;
  label?: string;
  listboxId: string;
  open: boolean;
  placeholder: string;
  selected?: ComboboxOption;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  successText?: string;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

function ComboboxShell({
  ariaLabel,
  className,
  disabled,
  displayLabel,
  dropdown,
  errorText,
  handleKeyDown,
  helperText,
  label,
  listboxId,
  open,
  placeholder,
  selected,
  setOpen,
  successText,
  triggerRef,
}: ComboboxShellProps) {
  return (
    <label className={cn("block space-y-1.5", className)}>
      {label && (
        <span className="text-xs font-semibold text-[var(--v2-text-strong)]">
          {label}
        </span>
      )}
      <div className="relative">
        <ButtonV2
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen(!open)}
          onKeyDown={handleKeyDown}
          className={cn(
            "v2-bg-surface v2-focus-ring flex min-h-10 w-full items-center justify-between gap-2 rounded-[var(--v2-radius-md)] border px-3 text-sm transition-colors",
            errorText
              ? "border-red-300"
              : successText
                ? "border-emerald-300"
                : open
                  ? "border-[var(--v2-control-accent,var(--v2-accent))]"
                  : "border-[var(--v2-border)]",
            disabled && "cursor-not-allowed opacity-50"
          )}
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          variant="outline"
        >
          <span
            className={cn(
              "truncate text-left",
              displayLabel
                ? "text-[var(--v2-text-strong)]"
                : "text-[var(--v2-text-muted)]"
            )}
          >
            {displayLabel ? (
              <span className="flex items-center gap-2">
                {selected?.icon}
                {displayLabel}
              </span>
            ) : (
              placeholder
            )}
          </span>
          <svg
            className={cn(
              "h-4 w-4 shrink-0 text-[var(--v2-text-muted)] transition-transform",
              open && "rotate-180"
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </ButtonV2>
        {dropdown}
      </div>
      {errorText && <span className="text-xs text-red-600">{errorText}</span>}
      {!errorText && helperText && (
        <span className="text-xs text-[var(--v2-text-muted)]">
          {helperText}
        </span>
      )}
      {!errorText && !helperText && successText ? (
        <span className="text-xs text-emerald-600">{successText}</span>
      ) : null}
    </label>
  );
}

function filterComboboxOptions(
  options: ComboboxOption[],
  search: string,
  isAsync: boolean
) {
  if (isAsync || !search.trim()) return options;
  const query = search.toLowerCase();
  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(query) ||
      option.description?.toLowerCase().includes(query) ||
      option.group?.toLowerCase().includes(query)
  );
}

function useResetComboboxHighlight(
  filteredLength: number,
  search: string,
  setHighlightIndex: React.Dispatch<React.SetStateAction<number>>
) {
  React.useEffect(
    () => setHighlightIndex(-1),
    [filteredLength, search, setHighlightIndex]
  );
}

function handleComboboxKeyDown(
  event: React.KeyboardEvent,
  context: {
    allowCustomValue: boolean;
    filtered: ComboboxOption[];
    highlightIndex: number;
    open: boolean;
    onSearch?: (_query: string) => void;
    search: string;
    selectOption: (_value: string) => void;
    setHighlightIndex: React.Dispatch<React.SetStateAction<number>>;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSearch: React.Dispatch<React.SetStateAction<string>>;
  }
) {
  if (!context.open) {
    if (["Enter", "ArrowDown", " "].includes(event.key)) {
      event.preventDefault();
      context.setOpen(true);
    }
    return;
  }
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    const direction = event.key === "ArrowDown" ? 1 : -1;
    context.setHighlightIndex((index) =>
      Math.max(0, Math.min(index + direction, context.filtered.length - 1))
    );
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    const selectedValue = context.filtered[context.highlightIndex]?.value;
    if (selectedValue) context.selectOption(selectedValue);
    else if (context.allowCustomValue && context.search.trim()) {
      context.selectOption(context.search.trim());
    }
    return;
  }
  if (event.key === "Escape") {
    event.preventDefault();
    context.setOpen(false);
    context.setSearch("");
    context.onSearch?.("");
  }
}

interface ComboboxDropdownContext {
  allowCustomValue: boolean;
  dropdownStyle: React.CSSProperties;
  emptyText: string;
  filtered: ComboboxOption[];
  handleKeyDown: (_event: React.KeyboardEvent) => void;
  highlightIndex: number;
  inputRef: React.RefObject<HTMLInputElement>;
  listRef: React.RefObject<HTMLUListElement>;
  listboxId: string;
  onSearch?: (_query: string) => void;
  open: boolean;
  search: string;
  searchPlaceholder: string;
  selectOption: (_value: string) => void;
  setHighlightIndex: React.Dispatch<React.SetStateAction<number>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  value: string;
}

function ComboboxDropdownPortal({
  allowCustomValue,
  dropdownStyle,
  emptyText,
  filtered,
  handleKeyDown,
  highlightIndex,
  inputRef,
  listRef,
  listboxId,
  onSearch,
  open,
  search,
  searchPlaceholder,
  selectOption,
  setHighlightIndex,
  setSearch,
  value,
}: ComboboxDropdownContext) {
  const dropdown = open
    ? ReactDOM.createPortal(
        <div
          data-combobox-portal
          style={dropdownStyle}
          className="v2-bg-surface overflow-hidden rounded-[var(--v2-radius-md)] border border-[var(--v2-border)] text-[var(--v2-text-strong)] shadow-lg"
        >
          {/* Search input */}
          <div className="border-b border-[var(--v2-border)] p-2">
            <div className="v2-bg-surface-muted flex items-center gap-2 rounded-md border border-[var(--v2-border)] px-2.5">
              <svg
                className="h-3.5 w-3.5 shrink-0 text-[var(--v2-text-muted)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={inputRef}
                type="text"
                className="w-full border-0 bg-transparent py-1.5 text-sm text-[var(--v2-text-strong)] outline-none placeholder:text-[var(--v2-text-muted)]"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  onSearch?.(e.target.value);
                }}
                onKeyDown={handleKeyDown}
              />
              {search && (
                <ButtonV2
                  type="button"
                  onClick={() => setSearch("")}
                  className="text-xs text-[var(--v2-text-muted)] hover:text-[var(--v2-text-strong)]"
                  variant="ghost"
                  size="sm"
                >
                  ✕
                </ButtonV2>
              )}
            </div>
          </div>

          {/* Options list */}
          <ul
            id={listboxId}
            ref={listRef}
            role="listbox"
            className="max-h-64 overflow-y-auto"
          >
            {filtered.length === 0 ? (
              allowCustomValue && search.trim() ? (
                <li
                  className="v2-action-neutral flex cursor-pointer items-center gap-2 px-3 py-2.5 text-sm"
                  onClick={() => selectOption(search.trim())}
                >
                  <span className="text-[var(--v2-text-muted)]">+</span>
                  <span className="text-[var(--v2-text-strong)]">
                    &quot;{search.trim()}&quot; ekle
                  </span>
                </li>
              ) : (
                <li className="px-3 py-4 text-center text-sm text-[var(--v2-text-muted)]">
                  {emptyText}
                </li>
              )
            ) : (
              filtered.map((opt, i) => {
                const isSelected = opt.value === value;
                const isHighlighted = i === highlightIndex;
                const showGroup =
                  i === 0 || filtered[i - 1]?.group !== opt.group;
                return (
                  <React.Fragment key={opt.value}>
                    {showGroup && opt.group ? (
                      <li
                        role="presentation"
                        className="v2-bg-surface-muted sticky top-0 z-[1] border-y border-[var(--v2-border)] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--v2-text-muted)] backdrop-blur"
                      >
                        {opt.group}
                      </li>
                    ) : null}
                    <li
                      role="option"
                      aria-selected={isSelected}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm transition-colors",
                        isHighlighted && "v2-option-active",
                        isSelected && !isHighlighted && "v2-option-active",
                        !isHighlighted && !isSelected && "v2-action-neutral"
                      )}
                      onClick={() => selectOption(opt.value)}
                      onMouseEnter={() => setHighlightIndex(i)}
                    >
                      {opt.icon && (
                        <span className="shrink-0 text-base">{opt.icon}</span>
                      )}
                      <div className="min-w-0 flex-1">
                        <span
                          className={cn(
                            "block truncate",
                            isSelected
                              ? "font-semibold text-[var(--v2-control-accent,var(--v2-accent))]"
                              : "text-[var(--v2-text-strong)]"
                          )}
                        >
                          {opt.label}
                        </span>
                        {opt.description && (
                          <span className="block truncate text-xs text-[var(--v2-text-muted)]">
                            {opt.description}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <svg
                          className="h-4 w-4 shrink-0 text-[var(--v2-control-accent,var(--v2-accent))]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </li>
                  </React.Fragment>
                );
              })
            )}
          </ul>
        </div>,
        document.body
      )
    : null;
  return dropdown;
}

export function ComboboxV2({
  options,
  value,
  onChange,
  placeholder = "Seçiniz...",
  searchPlaceholder = "Ara...",
  label,
  ariaLabel,
  helperText,
  errorText,
  successText,
  emptyText = "Sonuç bulunamadı",
  disabled = false,
  className,
  onSearch,
  isAsync = false,
  allowCustomValue = false,
}: ComboboxV2Props) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [highlightIndex, setHighlightIndex] = React.useState(-1);
  const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>(
    {}
  );
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const listboxId = React.useId();

  const selected = options.find((o) => o.value === value);
  const displayLabel =
    selected?.label ?? (allowCustomValue && value ? value : null);

  const filtered = React.useMemo(
    () => filterComboboxOptions(options, search, isAsync),
    [options, search, isAsync]
  );
  useResetComboboxHighlight(filtered.length, search, setHighlightIndex);

  // Calculate portal dropdown position from trigger button
  const updateDropdownPosition = React.useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = Math.min(320, spaceBelow - 8);
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      maxHeight: dropdownHeight,
      zIndex: 9999,
    });
  }, []);

  // Click outside → close
  React.useEffect(() => {
    function handler(e: MouseEvent) {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.closest("label")?.contains(target) &&
        !(target as HTMLElement).closest("[data-combobox-portal]")
      ) {
        setOpen(false);
        setSearch("");
        onSearch?.("");
      }
    }
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onSearch]);

  // Reposition on scroll/resize while open
  React.useEffect(() => {
    if (!open) return;
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [open, updateDropdownPosition]);

  // Focus input when opened
  React.useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const el =
        listRef.current.querySelectorAll<HTMLElement>('[role="option"]')[
          highlightIndex
        ];
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex]);

  const selectOption = (val: string) => {
    onChange(val);
    setOpen(false);
    setSearch("");
    onSearch?.("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) =>
    handleComboboxKeyDown(event, {
      allowCustomValue,
      filtered,
      highlightIndex,
      open,
      onSearch,
      search,
      selectOption,
      setHighlightIndex,
      setOpen,
      setSearch,
    });

  const dropdown = (
    <ComboboxDropdownPortal
      allowCustomValue={allowCustomValue}
      dropdownStyle={dropdownStyle}
      emptyText={emptyText}
      filtered={filtered}
      handleKeyDown={handleKeyDown}
      highlightIndex={highlightIndex}
      inputRef={inputRef}
      listRef={listRef}
      listboxId={listboxId}
      onSearch={onSearch}
      open={open}
      search={search}
      searchPlaceholder={searchPlaceholder}
      selectOption={selectOption}
      setHighlightIndex={setHighlightIndex}
      setSearch={setSearch}
      value={value}
    />
  );

  return (
    <ComboboxShell
      ariaLabel={ariaLabel}
      className={className}
      disabled={disabled}
      displayLabel={displayLabel}
      dropdown={dropdown}
      errorText={errorText}
      handleKeyDown={handleKeyDown}
      helperText={helperText}
      label={label}
      listboxId={listboxId}
      open={open}
      placeholder={placeholder}
      selected={selected}
      setOpen={setOpen}
      successText={successText}
      triggerRef={triggerRef}
    />
  );
}
